import { createContext, useContext, useState, useEffect, useRef } from 'react';

const StepContext = createContext();

function getStepUrl(basePath, step) {
  return step === 0 ? basePath : `${basePath}-step${step + 1}/`;
}

export function StepProvider({
  children,
  initialStep = 0,
  totalSteps = 6,
  canProceed,
}) {
  const [activeStep, setActiveStep] = useState(initialStep);
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === totalSteps - 1;
  // Determine if we can proceed from the current step
  let canProceedCurrentStep = true;
  if (typeof canProceed === 'function') {
    canProceedCurrentStep = canProceed(activeStep);
  } else if (Array.isArray(canProceed)) {
    canProceedCurrentStep = canProceed[activeStep]
      ? canProceed[activeStep]()
      : true;
  }

  // Capture the base path once on mount so we can set the step URLs correctly
  const basePathRef = useRef(
    window.location.pathname.replace(/\/$/, '').replace(/-step\d+$/, '')
  );

  useEffect(() => {
    // Tag the initial history entry so popstate can restore the step
    window.history.replaceState(
      { step: initialStep },
      '',
      window.location.href
    );

    // Listen for back/forward navigation to update the active step
    const handlePopState = e => {
      const step = e.state?.step ?? 0;
      setActiveStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Add popstate listener and clean up on unmount
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [initialStep]);

  const handleNext = () => {
    if (!isLastStep && canProceedCurrentStep) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      window.history.pushState(
        { step: nextStep },
        '',
        getStepUrl(basePathRef.current, nextStep)
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      const prevStep = activeStep - 1;
      setActiveStep(prevStep);
      window.history.pushState(
        { step: prevStep },
        '',
        getStepUrl(basePathRef.current, prevStep)
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <StepContext.Provider
      value={{
        activeStep,
        setActiveStep,
        isFirstStep,
        isLastStep,
        handleNext,
        handlePrev,
        totalSteps,
        canProceedCurrentStep,
      }}
    >
      {children}
    </StepContext.Provider>
  );
}

export function useStep() {
  return useContext(StepContext);
}
